<script setup lang="ts">
import { computed, inject } from 'vue';
import { NxDateInput, NxSelectInput, NxTextInput, NxTimeInput } from '../Inputs';
import { i18nInjectionKey } from '../internal';
import {
  filterOperatorArity,
  restrictedFilterOperators,
  type NxTableFilterCondition,
  type NxTableFilterKind,
  type NxTableFilterOperator,
} from './filterModel';

/**
 * Operator select + kind-specific value inputs for ONE filter condition.
 * Shared by the header filter popover and the filter wizard.
 */
const props = withDefaults(
  defineProps<{
    kind: NxTableFilterKind;
    /** Options for `kind: 'select'` value pickers. */
    options?: string[];
    /** Display label for a select option value (e.g. enum code → label). */
    optionLabel?: (value: string) => string;
    /** Restricts the operator list (a column's `filterOperators`); all of the kind by default. */
    allowedOperators?: readonly NxTableFilterOperator[];
    disabled?: boolean;
  }>(),
  {
    options: () => [],
    optionLabel: undefined,
    allowedOperators: undefined,
    disabled: false,
  }
);

const condition = defineModel<NxTableFilterCondition>({ required: true });

const i18n = inject(i18nInjectionKey)!;

const operators = computed(() => restrictedFilterOperators(props.kind, props.allowedOperators));
const arity = computed(() => filterOperatorArity(condition.value.operator));

const operatorLabel = (operator: NxTableFilterOperator) =>
  i18n.t(`common.table.filter.op.${operator}`);

const boolOptions = ['true', 'false'];
const boolLabel = (option: string) =>
  option === 'true'
    ? i18n.t('common.table.filter.bool_true')
    : i18n.t('common.table.filter.bool_false');

function setOperator(operator: NxTableFilterOperator | null) {
  if (!operator) return;
  const next: NxTableFilterCondition = { ...condition.value, operator };
  const nextArity = filterOperatorArity(operator);
  if (nextArity === 'none') {
    delete next.value;
    delete next.valueTo;
    delete next.values;
  } else if (nextArity === 'multi') {
    delete next.value;
    delete next.valueTo;
  } else {
    delete next.values;
    if (nextArity === 'single') delete next.valueTo;
  }
  condition.value = next;
}

function setValue(value: string | null) {
  condition.value = { ...condition.value, value: value ?? '' };
}
function setValueTo(value: string | null) {
  condition.value = { ...condition.value, valueTo: value ?? '' };
}
function setValues(values: string[] | [null]) {
  condition.value = {
    ...condition.value,
    values: values.filter((v): v is string => v !== null && v !== ''),
  };
}
</script>

<template>
  <div class="condition-inputs">
    <NxSelectInput
      class="operator"
      :model-value="[condition.operator]"
      :options="operators"
      :option-text="operatorLabel"
      :placeholder="i18n.t('common.table.filter.operator')"
      height="xs"
      :disabled="props.disabled"
      @update:model-value="(v: (NxTableFilterOperator | null)[]) => setOperator(v[0] ?? null)"
    >
      <template #option="{ option }">{{ operatorLabel(option) }}</template>
    </NxSelectInput>

    <template v-if="arity === 'single'">
      <NxDateInput
        v-if="props.kind === 'date'"
        class="value"
        :model-value="condition.value ?? null"
        height="xs"
        :disabled="props.disabled"
        @update:model-value="setValue"
      />
      <NxTimeInput
        v-else-if="props.kind === 'time'"
        class="value"
        :model-value="condition.value ?? null"
        height="xs"
        :disabled="props.disabled"
        @update:model-value="setValue"
      />
      <NxSelectInput
        v-else-if="props.kind === 'bool'"
        class="value"
        :model-value="condition.value ? [condition.value] : []"
        :options="boolOptions"
        :option-text="boolLabel"
        :placeholder="i18n.t('common.table.filter.value')"
        height="xs"
        :disabled="props.disabled"
        @update:model-value="(v: (string | null)[]) => setValue(v[0] ?? null)"
      >
        <template #option="{ option }">{{ boolLabel(option) }}</template>
      </NxSelectInput>
      <NxTextInput
        v-else
        class="value"
        :model-value="condition.value ?? ''"
        :placeholder="i18n.t('common.table.filter.value')"
        height="xs"
        :disabled="props.disabled"
        :inputmode="props.kind === 'number' || props.kind === 'currency' ? 'decimal' : undefined"
        @update:model-value="setValue"
      />
    </template>

    <template v-else-if="arity === 'range'">
      <template v-if="props.kind === 'date'">
        <NxDateInput
          class="value"
          :model-value="condition.value ?? null"
          height="xs"
          :disabled="props.disabled"
          @update:model-value="setValue"
        />
        <NxDateInput
          class="value"
          :model-value="condition.valueTo ?? null"
          height="xs"
          :disabled="props.disabled"
          @update:model-value="setValueTo"
        />
      </template>
      <template v-else-if="props.kind === 'time'">
        <NxTimeInput
          class="value"
          :model-value="condition.value ?? null"
          height="xs"
          :disabled="props.disabled"
          @update:model-value="setValue"
        />
        <NxTimeInput
          class="value"
          :model-value="condition.valueTo ?? null"
          height="xs"
          :disabled="props.disabled"
          @update:model-value="setValueTo"
        />
      </template>
      <template v-else>
        <NxTextInput
          class="value"
          :model-value="condition.value ?? ''"
          :placeholder="i18n.t('common.table.filter.value_from')"
          height="xs"
          :disabled="props.disabled"
          inputmode="decimal"
          @update:model-value="setValue"
        />
        <NxTextInput
          class="value"
          :model-value="condition.valueTo ?? ''"
          :placeholder="i18n.t('common.table.filter.value_to')"
          height="xs"
          :disabled="props.disabled"
          inputmode="decimal"
          @update:model-value="setValueTo"
        />
      </template>
    </template>

    <NxSelectInput
      v-else-if="arity === 'multi'"
      class="value"
      :model-value="condition.values ?? []"
      :options="props.options"
      multiple
      :option-text="props.optionLabel"
      :placeholder="i18n.t('common.table.filter.value')"
      height="xs"
      :disabled="props.disabled"
      @update:model-value="setValues"
    >
      <template v-if="$slots.option || props.optionLabel" #option="slotProps">
        <slot name="option" v-bind="slotProps">
          {{ props.optionLabel?.(slotProps.option) ?? slotProps.option }}
        </slot>
      </template>
    </NxSelectInput>
  </div>
</template>

<style scoped>
.condition-inputs {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  flex: 1 1 auto;
}
.operator {
  flex: 0 0 calc(var(--nxt-size-unit) * 8.5);
  min-width: 0;
}
.value {
  flex: 1 1 0;
  min-width: 0;
}
</style>
